/**
 * World sizing. These are LOCKED in docs/ARCHITECTURE.md §2 — every subsystem
 * derives from them, so changing one is an orchestrator decision, not a
 * worker's. Vertex packing in particular assumes CHUNK_SIZE fits in 6 bits.
 *
 * All lengths here are in VOXELS, not metres. VOXEL_SIZE converts once, at the
 * render transform.
 */

/**
 * Edge length of one terrain voxel in metres — eight voxels per metre.
 *
 * This is the defining property of the look: the references are a dense
 * micro-voxel world, not one-metre blocks (docs/ART-DIRECTION.md §0).
 *
 * Measured off the references rather than guessed: in the cave capture
 * (vibeshot 01-55-32) the central rock pillar spans ~12 voxels across roughly
 * 1.5 m; the house wall (ss_0a2cb8d8) gives ~10/m on its stone courses. Eight
 * is the conservative read of both. A tree trunk is then 12-16 voxels across, a
 * grass blade 1-2 voxels wide and 4-12 tall.
 */
export const VOXEL_SIZE = 0.125;

/**
 * Held items and small props are modelled at twice terrain resolution — the
 * torch flame and hammer head in the cave capture are visibly finer than the
 * cave wall behind them. These are separate voxel models, not world voxels.
 */
export const ITEM_VOXEL_SIZE = VOXEL_SIZE / 2;

/** Voxels per metre. */
export const VOXELS_PER_METRE = 1 / VOXEL_SIZE;

/** Sub-chunk edge in voxels. Must stay ≤ 64 for the packed vertex format. */
export const CHUNK_SIZE = 32;

/** Voxels per sub-chunk. */
export const CHUNK_VOLUME = CHUNK_SIZE ** 3;

/** World edge of one sub-chunk, in metres. */
export const CHUNK_METRES = CHUNK_SIZE * VOXEL_SIZE;

/** Vertical sub-chunks per column. */
export const COLUMN_HEIGHT = 64;

/** World height in voxels (256 m at 8 voxels/m). */
export const WORLD_HEIGHT = CHUNK_SIZE * COLUMN_HEIGHT;

/** Sea level, in voxels (64 m). */
export const SEA_LEVEL = 512;

/** Alpha world extent in voxels per axis (1024 m). Finite by design. */
export const WORLD_EXTENT = 8192;

/** Simulation ticks per second. Rendering is decoupled and interpolates. */
export const TICK_RATE = 20;

/** Seconds per simulation tick. */
export const TICK_SECONDS = 1 / TICK_RATE;

/** Light channels are 4 bits each. */
export const MAX_LIGHT = 15;

/**
 * Direction *toward* the sun, normalised. Shared because the generator bakes
 * shadows against it and the renderer shades against it, and a shadow cast in
 * one direction while the light comes from another is worse than no shadow.
 *
 * Low and to one side: a high sun casts shadows straight down, where nothing
 * can see them. The references are lit at a raking angle.
 */
export const SUN_DIRECTION = Object.freeze({ x: 0.46, y: 0.72, z: 0.52 });
